import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import FabricLoader from "../fabricloader";
import settings from "../settings";
import { concatFilters, getClientProps } from "../utils";

import get from "lodash/get";
import isArray from "lodash/isArray";
import shuffle from "lodash/shuffle";
import isEmpty from "lodash/isEmpty";

const {
  baseApiUrl,
  operator,
  shuffledSwimlanes = [],
  vaixClientId,
  recsFields,
  internalRecsFields,
  recsFilters,
} = settings;

const operationIds = {
  recommended: "get_recommended_games",
  hot: "get_hot_games",
  trending: "get_trending_games",
  popular: "get_popular_games",
  combined: "get_combined_games",
  similar: "get_similar_games",
  new: "get_new_games",
  latest: "get_latest_game_sessions",
};

const preRequestsProps = {
  favoriteStudio: {
    fetchUrl: "/game_studios/recommended",
    fieldName: "game_studio",
    dataKey: "game_studio",
    reqFilters: `game_studio:nin:"";game_studio:nin:null`,
    filterOperator: "in",
  },
  favoriteTag: {
    fetchUrl: "/game_tags/recommended",
    fieldName: "vaix_tags",
    dataKey: "game_tag",
    reqFilters: 'vaix_tags:nin:""',
    filterOperator: "in",
    getReqParams: (user) => {
      const { refDate } = user || {};
      return refDate ? { ref_date: refDate } : {};
    },
  },
  userCountry: {
    fetchUrl: "/players",
    fieldName: "user_country",
    dataKey: "country",
    filterOperator: "eq",
  },
};

export const recsApi = createApi({
  reducerPath: "recommendationsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: baseApiUrl,
    prepareHeaders: (headers, { getState }) => {
      const { apiAuthToken } = getClientProps();
      headers.set("authorization", `Bearer ${apiAuthToken}`);
      vaixClientId && headers.set("x-vaix-client-id", vaixClientId);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getRecommendations: builder.query({
      async queryFn(
        { user, fetchUrl, args, prerequisiteRequests },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        //for each extra request  get the result and add it to filters
        let extraRequestFilters = [];
        let encounteredRequestError = false;
        let requestedFieldValue;

        const userId = user?.userId;

        if (prerequisiteRequests) {
          for (const request of prerequisiteRequests) {
            const currentRequestInfo = preRequestsProps[request];

            const {
              fetchUrl,
              filterOperator,
              fieldName,
              reqFilters,
              dataKey,
              getReqParams,
            } = currentRequestInfo;

            const userIdKey = request === "userCountry" ? "id" : "user";
            const reqParams = getReqParams ? getReqParams(user) : {};

            const { data: requestData, error: requestError } =
              await fetchWithBQ({
                url: `/api/casino${fetchUrl}`,
                params: {
                  [userIdKey]: userId,
                  count: 1,
                  filters: reqFilters,
                  operator,
                  ...reqParams,
                },
              });

            if (requestError) {
              encounteredRequestError = true;
              break;
            }

            requestedFieldValue = isArray(requestData.data)
              ? get(requestData, `data[0][${dataKey}]`)
              : get(requestData, `data[${dataKey}]`);

            extraRequestFilters.push({
              field: fieldName,
              filterValue: requestedFieldValue,
              operator: filterOperator,
            });
          }
        }

        if (encounteredRequestError) return { results: null };

        const requestType = fetchUrl?.split("/").at(-1);

        const defaultFilters = "game_image:in:desktop,mobile";

        let filters = args?.filters || "";

        filters =
          extraRequestFilters &&
          concatFilters(args?.filters, extraRequestFilters);

        if (recsFilters) {
          filters = isEmpty(filters)
            ? filters.concat(recsFilters)
            : filters.concat(";", recsFilters);
        }

        const requiresLatestGamesInfo =
          requestType === "similar" || requestType === "combined";

        if (requiresLatestGamesInfo && (userId === "0" || !userId))
          return { results: null };

        const fields = "game_id,game_name";
        const internal = "images";

        const queryParams = {
          ...args,
          user: userId,
          fields: recsFields ? fields.concat(",", recsFields) : fields,
          internal_fields: internalRecsFields
            ? internal.concat(",", internalRecsFields)
            : internal,
          operator,
          count: args?.count ?? 24,
          filters: filters
            ? defaultFilters.concat(";", filters)
            : defaultFilters,
          invalidate_cache: true,
        };

        let latestGameId = "";
        let latestGameName = "";
        if (requiresLatestGamesInfo) {
          const { data: latestGames, error: latestGamesError } =
            await fetchWithBQ({
              url: "/api/casino/game_sessions/latest",
              params: {
                user: userId,
                count: 1,
                fields: "game_name,game_id",
                operator,
              },
            });

          if (latestGamesError) return { error: latestGamesError };

          const latestGame = latestGames?.data[0] || {};
          latestGameId = latestGame.game_id;
          latestGameName = latestGame.game_name;

          const key = requestType === "similar" ? "game_ids" : "game_id";
          queryParams[key] = latestGameId;
        }

        const { data, error } = await FabricLoader.load({
          operation_id: operationIds[requestType] || "get_recommended_games",
          name: args?.key || JSON.stringify(queryParams),
          params: queryParams,
        });

        const shouldShuffleResults =
          shuffledSwimlanes?.includes(requestType) ||
          prerequisiteRequests?.some((req) => shuffledSwimlanes.includes(req));

        const result = data
          ? {
              data: {
                results: shouldShuffleResults ? shuffle(data) : data,
                similar_game_name: latestGameName,
                titleSuffix: requestedFieldValue ?? null,
              },
            }
          : { error };

        return result;
      },
    }),
    getTags: builder.query({
      query: (userId) => ({
        url: "/api/casino/game_tags/recommended",
        params: {
          user: userId,
          filters: 'vaix_tags:nin:""',
          count: 10,
          operator,
        },
      }),
    }),
    getCategories: builder.query({
      query: (userId) => ({
        url: "/api/casino/categories/recommended",
        params: {
          user: userId,
          filters: "category:neq:null;category:neq:",
          count: 10,
          operator,
        },
      }),
    }),
  }),
});
